import AddIcon from '@mui/icons-material/Add';
import { Button, Stack } from '@mui/material';
import { type GridApiPro, GridRow } from '@mui/x-data-grid-pro';
import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import { CustomerOverview } from '@vy/api-definition';
import { getSelectorAttribute, customersOverviewPageSelectors } from '@vy/app-selectors';
import { CUSTOMERS_COLUMNS_KEYS } from '@vy/customers-types';
import { TABLE_NAMES } from '@vy/types';
import { isEmptyArray } from '@vy/utils';

const CUSTOMERS_DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  [CUSTOMERS_COLUMNS_KEYS.ERP_ID]: false,
  [CUSTOMERS_COLUMNS_KEYS.CRM_ID]: false,
  [CUSTOMERS_COLUMNS_KEYS.CREATED_AT]: false,
  [CUSTOMERS_COLUMNS_KEYS.UPDATED_AT]: false,
  [CUSTOMERS_COLUMNS_KEYS.TAX_ID]: false,
  [CUSTOMERS_COLUMNS_KEYS.TAX_TYPE]: false,
  [CUSTOMERS_COLUMNS_KEYS.WALLET]: false,
  [CUSTOMERS_COLUMNS_KEYS.ALIASES]: false,
};

import { VyTableWithFilters } from '../../../../components/new-design/table/vy-table-with-filters';
import type { VyTableMinimalProps } from '../../../../components/new-design/table/vy-table/vy-table-types';
import { useCustomFieldDefinitions } from '../../../../hooks/requests/use-custom-field-definitions';
import { useSubsidiaries } from '../../../../hooks/requests/use-subsidiaries';
import { createCustomFieldColumns } from '../../../../utils/create-custom-field-columns';
import { extractCustomFieldNamesFromConfig } from '@vy/custom-fields-display';
import { extractUniqueCustomFieldNames } from '../../../../utils/extract-unique-custom-field-names';
import { CUSTOM_FIELD_ENTITIES } from '@vy/custom-fields-types';
import { createCustomersColumns } from '../customers-page-consts/customers-columns';
import { useCustomersActions } from '../customers-page-hooks/use-customers-actions';
import { useCustomersFilterFields } from '../customers-page-hooks/use-customers-filter-fields';
import { useCustomersFilters } from '../customers-page-hooks/use-customers-filters';

type CustomersTableProps = Omit<VyTableMinimalProps<CustomerOverview>, 'tableName'> & {
  apiRef: React.MutableRefObject<GridApiPro>;
  filters: ReturnType<typeof useCustomersFilters>;
};

export const CustomersTable: React.FC<CustomersTableProps> = ({ apiRef, filters, ...props }) => {
  const { data: subsidiaries } = useSubsidiaries();
  const { data: customFieldDefinitionsData } = useCustomFieldDefinitions();
  const filterFields = useCustomersFilterFields();

  const { handleExportSelectedCustomers } = useCustomersActions({
    selectedCustomers: [],
    apiRef,
  });

  const defaultSubsidiary = useMemo(() => {
    return subsidiaries?.subsidiaries.find((subsidiary) => subsidiary.isPrimary)?.name;
  }, [subsidiaries]);

  const baseFields = useMemo(() => new Set([...Object.values(CUSTOMERS_COLUMNS_KEYS), 'actionMenu']), []);

  const customFieldNamesSignature = useMemo(() => {
    const dataCustomNames = extractUniqueCustomFieldNames(
      (props.items ?? []).map((item) => item.customFieldValues),
    );
    return [...new Set(dataCustomNames)].sort().join(',');
  }, [props.items, baseFields]);

  const definitionCustomNames = useMemo(() => {
    const allDefinitions = customFieldDefinitionsData?.customFieldDefinitions ?? [];
    return allDefinitions
      .filter((def) => def.vayuEntity === CUSTOM_FIELD_ENTITIES.CUSTOMER)
      .map((def) => def.vayuCustomFieldName);
  }, [customFieldDefinitionsData]);

  const columns = useMemo(() => {
    const baseColumns = createCustomersColumns(defaultSubsidiary);

    const configCustomNames = extractCustomFieldNamesFromConfig([], baseFields);

    const dataCustomNames = extractUniqueCustomFieldNames(
      (props.items ?? []).map((item) => item.customFieldValues),
    );

    const customFieldNames = [...new Set([...configCustomNames, ...dataCustomNames, ...definitionCustomNames])];

    if (isEmptyArray(customFieldNames)) {
      return baseColumns;
    }

    const customFieldColumns = createCustomFieldColumns<CustomerOverview>(customFieldNames);

    return [...baseColumns, ...customFieldColumns];
  }, [defaultSubsidiary, customFieldNamesSignature, definitionCustomNames]);

  return (
    <VyTableWithFilters
      {...getSelectorAttribute(customersOverviewPageSelectors.table)}
      apiRef={apiRef}
      tableName={TABLE_NAMES.CUSTOMERS}
      columns={columns}
      search={{
        value: filters.query ?? '',
        onChange: filters.handleSearchInputChange,
        placeholder: 'Search by Name, Email, etc...',
      }}
      filters={{
        fields: filterFields,
        onFiltersChange: filters.handleFilterClick,
        defaultFilters: filters.filters ?? [],
      }}
      columnSettings={{
        excludedFields: ['actionMenu'],
        mandatoryColumnsCount: 2,
      }}
      tableColumnsApiConfig={{ visibility: CUSTOMERS_DEFAULT_COLUMN_VISIBILITY }}
      onDownload={handleExportSelectedCustomers}
      isFilterOpen={filters.isFilterCustomersOpen}
      actions={
        <Stack direction="row" sx={{
          alignItems: "flex-end"
        }}>
          <Button
            sx={{
              columnGap: '12px',
              borderRadius: '4px',
            }}
            variant="contained"
            component={NavLink}
            to="create"
            {...getSelectorAttribute(customersOverviewPageSelectors.createCustomerButton)}
          >
            <AddIcon />
            Customer
          </Button>
        </Stack>
      }
      disableRowSelectionOnClick
      rowsPerPageOptions={[10, 25, 50, 100, 500]}
      disableColumnMenu
      getRowId={(row) => row.id}
      slots={{
        row: (rowProps) => {
          const { id } = rowProps.row;

          return (
            <NavLink to={`/customers/${id}/overview`}>
              <GridRow {...rowProps} />
            </NavLink>
          );
        },
      }}
      {...props}
    />
  );
};
